import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { rpc } from "../../lib/rpc";
import { AdminPageHeader, DataTable } from "../../components/admin/DataTable";

const dayMs = 24 * 60 * 60 * 1000;

function daysSince(iso) {
    if (!iso) return null;
    return Math.floor((Date.now() - new Date(iso).getTime()) / dayMs);
}

export default function AdminAusleihen() {
    const [loans, setLoans] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        rpc.get("/loans").then((r) => {
            if (r.ok) {
                setLoans(r.data);
                setError(null);
            } else {
                // Fehler nicht als "keine Ausleihen" tarnen (Review PR #48).
                setError(r.error || "Ausleihen konnten nicht geladen werden.");
            }
        });
    }, []);

    // #136 Befund 1: Leitstand-Kachel "Überfällig" verlinkt mit ?overdue=1.
    const [searchParams, setSearchParams] = useSearchParams();
    const onlyOverdue = searchParams.get("overdue") === "1";

    const overdueCount = loans.filter((l) => l.overdue).length;
    const visibleLoans = onlyOverdue ? loans.filter((l) => l.overdue) : loans;

    const columns = [
        { key: "article_name", label: "Artikel", render: (r) => r.article_name || "—" },
        {
            key: "serial_number",
            label: "Kennung",
            render: (r) => (
                <span className="font-mono text-xs">{r.serial_number || r.asset_tag || "—"}</span>
            ),
        },
        { key: "holder", label: "Ausgeliehen an", render: (r) => r.holder || "—" },
        {
            key: "loaned_at",
            label: "Seit",
            render: (r) => {
                const d = daysSince(r.loaned_at);
                return r.loaned_at
                    ? `${new Date(r.loaned_at).toLocaleDateString("de-DE")} (${d} ${d === 1 ? "Tag" : "Tage"})`
                    : "—";
            },
        },
        {
            key: "overdue",
            label: "Status",
            render: (r) =>
                r.overdue ? (
                    <span className="rounded-full border border-warning/50 px-2 py-0.5 text-xs font-semibold text-warning">
                        Überfällig
                    </span>
                ) : (
                    <span className="text-xs text-muted-foreground">offen</span>
                ),
        },
    ];

    return (
        <>
            <AdminPageHeader
                title="Ausleihen"
                subtitle={`${loans.length} offen${overdueCount ? ` · ${overdueCount} überfällig` : ""}`}
            />
            {error ? (
                <div
                    className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive"
                    data-testid="admin-ausleihen-error"
                >
                    Ausleihen konnten nicht geladen werden: {error}
                </div>
            ) : (
                <>
                    {onlyOverdue ? (
                        <div
                            className="mb-3 flex items-center gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs"
                            data-testid="ausleihen-overdue-filter"
                        >
                            <span className="flex-1">
                                Nur überfällige Ausleihen — {visibleLoans.length} von {loans.length}
                            </span>
                            <button
                                type="button"
                                onClick={() =>
                                    setSearchParams((prev) => {
                                        const next = new URLSearchParams(prev);
                                        next.delete("overdue");
                                        return next;
                                    }, { replace: true })
                                }
                                aria-label="Überfällig-Filter aufheben"
                                className="rounded-md border border-border px-2 py-1"
                                data-testid="ausleihen-overdue-filter-clear"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    ) : null}
                <DataTable
                    testId="admin-ausleihen-table"
                    columns={columns}
                    rows={visibleLoans}
                    rowKey={(r) => r.asset_id}
                    search={(r) => `${r.article_name || ""} ${r.serial_number || ""} ${r.asset_tag || ""} ${r.holder || ""}`}
                />
                </>
            )}
        </>
    );
}
